import type { RouterContext } from '@townsquare/router/routing-types';
import { createResource } from '@townsquare/router/resources/createResource';

import type { ResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

import { FIRST_PAGE_SIZE } from '../constants';
import { fetchPopularFeed } from '../hooks/fetch-popular-feed';
import { getDiscoverTab, getTimeframe, type PopularFeedResponse, type Timeframe } from '../types';

const getVariables = (
	routerContext: RouterContext,
	resourceContext: ResourceContext,
): { isEnabled: false } | { isEnabled: true; cloudId: string; timeframe: Timeframe } => {
	const { collabContext } = resourceContext;

	// If tabs are enabled and we're in a tab, disable. That's handled by label-feed-resource
	const discoverTab = getDiscoverTab(routerContext.query.tab);
	if (discoverTab !== 'popular' && fg('townsquare_enable_popular_tabs')) {
		return { isEnabled: false };
	}

	// If no cloudId (yet), disable
	if (!collabContext || !collabContext.rovoWorkspace) {
		return { isEnabled: false };
	}

	return {
		isEnabled: true,
		cloudId: collabContext.rovoWorkspace,
		timeframe: getTimeframe(routerContext.query.timeGranularity),
	};
};

export const popularFeedResource = createResource<PopularFeedResponse | undefined>({
	getData: async (routerContext, resourceContext) => {
		const variables = getVariables(routerContext, resourceContext as ResourceContext);

		if (!variables.isEnabled) {
			return undefined;
		}

		// Load longer first page of results
		return fetchPopularFeed(variables.cloudId, variables.timeframe, FIRST_PAGE_SIZE);
	},
	getKey: (routerContext, resourceContext) => {
		const variables = getVariables(routerContext, resourceContext as ResourceContext);

		if (!variables.isEnabled) {
			return `popular-feed-posts-disabled`;
		}

		return `popular-feed-posts-key-${variables.timeframe}-${variables.cloudId}`;
	},
	type: 'POPULAR_FEED_POSTS',
	maxAge: 60_000 * 30, // Cache for 30 minutes
});
